import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Loader2, Link as LinkIcon, FileCheck, ExternalLink, Trash2 } from 'lucide-react';
import { apiFetch } from '../../../utils/api';

interface ProjectDeliverablesProps {
    projectId: string;
}

const ProjectDeliverables: React.FC<ProjectDeliverablesProps> = ({ projectId }) => {
    const [deliverables, setDeliverables] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);

    const [newEntry, setNewEntry] = useState({
        name: '', description: '', file_url: ''
    });

    const fetchDeliverables = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/project-delivery/deliverables?project_id=${projectId}`);
            if (res.ok) setDeliverables(await res.json());
        } catch (err) { console.error(err); } finally { setLoading(false); }
    }, [projectId]);

    useEffect(() => {
        fetchDeliverables();
    }, [projectId, fetchDeliverables]);

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await apiFetch('/project-delivery/deliverables', {
                method: 'POST',
                body: JSON.stringify({ ...newEntry, project_id: projectId })
            });
            if (res.ok) {
                setShowAddModal(false);
                setNewEntry({ name: '', description: '', file_url: '' });
                fetchDeliverables();
            }
        } catch (err) { console.error(err); }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this deliverable?')) return;
        try {
            const res = await apiFetch(`/project-delivery/deliverables/${id}`, { method: 'DELETE' });
            if (res.ok) fetchDeliverables();
        } catch (err) { console.error(err); }
    };

    const handleUpdateStatus = async (id: string, status: string) => {
        try {
            const res = await apiFetch(`/project-delivery/deliverables/${id}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status })
            });
            if (res.ok) fetchDeliverables();
        } catch (err) { console.error(err); }
    };

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin text-blue-500" /></div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-800">Project Deliverables</h3>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm font-semibold"
                >
                    <Plus size={16} /> Add Deliverable
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {deliverables.length === 0 ? (
                    <div className="col-span-full text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-gray-500">
                        No deliverables uploaded yet.
                    </div>
                ) : (
                    deliverables.map((d, i) => (
                        <div key={i} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition">
                            <div className="flex items-center justify-between mb-4">
                                <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
                                    <FileCheck size={24} />
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <div className="flex items-center gap-2">
                                        <select
                                            className="text-[10px] bg-gray-50 border-none outline-none font-bold text-blue-600 rounded p-1"
                                            value={d.status}
                                            onChange={(e) => handleUpdateStatus(d.id, e.target.value)}
                                        >
                                            <option value="Pending">Pending</option>
                                            <option value="Submitted">Submitted</option>
                                            <option value="Approved">Approved</option>
                                            <option value="Rejected">Rejected</option>
                                        </select>
                                        <button
                                            onClick={() => handleDelete(d.id)}
                                            className="p-1 text-gray-400 hover:text-red-500 rounded transition"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${d.status === 'Approved' ? 'bg-green-100 text-green-700' :
                                        d.status === 'Rejected' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                                        }`}>
                                        {d.status}
                                    </span>
                                </div>
                            </div>
                            <h4 className="font-bold text-gray-800 mb-1">{d.name}</h4>
                            <p className="text-xs text-gray-500 mb-4 line-clamp-2">{d.description}</p>

                            {d.file_url && (
                                <a
                                    href={d.file_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-blue-600 text-xs font-bold hover:underline"
                                >
                                    <ExternalLink size={14} />
                                    View Resource
                                </a>
                            )}
                        </div>
                    ))
                )}
            </div>

            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full">
                        <h3 className="text-xl font-bold mb-4">Register Deliverable</h3>
                        <form onSubmit={handleAdd} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
                                <input className="w-full border p-2 rounded-lg" value={newEntry.name} onChange={e => setNewEntry({ ...newEntry, name: e.target.value })} required />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                                <textarea className="w-full border p-2 rounded-lg" rows={3} value={newEntry.description} onChange={e => setNewEntry({ ...newEntry, description: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Resource URL / File Link</label>
                                <div className="relative">
                                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    <input className="w-full border pl-10 p-2 rounded-lg" value={newEntry.file_url} onChange={e => setNewEntry({ ...newEntry, file_url: e.target.value })} placeholder="https://..." />
                                </div>
                            </div>
                            <div className="flex gap-4 pt-4">
                                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 px-4 py-2 border rounded-lg font-bold">Cancel</button>
                                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-bold">Create</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectDeliverables;
